import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { orderBy, QueryConstraint, where } from "firebase/firestore";
import { FirestoreService } from "../../../api/firebase/firestoreService";
import { FirestoreCollectionReference } from "../../../api/firebase/references";
import { MedicalPaOrder } from "../../../data-model/medicalPaOrdersModel";
import { logDataToConsole } from "../../../utils/customLogger";

interface MedicalPaOrdersState {
  medicalPaOrders: MedicalPaOrder[] | null;
  loading: boolean;
  error: string | null;
  lastVisible: any;
  showinLineLoader: boolean;
  totalDocuments: number;
  currentlyLoadedCount: number;
}

const initialState: MedicalPaOrdersState = {
  medicalPaOrders: null,
  loading: false,
  error: null,
  lastVisible: null,
  showinLineLoader: false,
  totalDocuments: 0,
  currentlyLoadedCount: 0,
};

const medicalPaOrdersSlice = createSlice({
  name: "medicalPaOrders",
  initialState,
  reducers: {
    setMedicalPaOrders: (state, action: PayloadAction<MedicalPaOrder[]>) => {
      state.medicalPaOrders = action.payload;
      state.currentlyLoadedCount = action.payload.length;
      state.loading = false;
      state.error = null;
    },
    appendMedicalPaOrders: (state, action: PayloadAction<MedicalPaOrder[]>) => {
      state.medicalPaOrders = state.medicalPaOrders
        ? [...state.medicalPaOrders, ...action.payload]
        : action.payload;
      state.currentlyLoadedCount = state.medicalPaOrders.length;
      state.loading = false;
      state.error = null;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
      if (action.payload) {
        state.error = null;
      }
    },
    setError: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
      state.loading = false;
    },
    setLastVisible: (state, action: PayloadAction<any>) => {
      state.lastVisible = action.payload;
    },
    setTotalDocuments: (state, action: PayloadAction<number>) => {
      state.totalDocuments = action.payload;
    },
    resetPagination: (state) => {
      state.lastVisible = null;
      state.currentlyLoadedCount = 0;
      state.medicalPaOrders = null;
      state.totalDocuments = 0;
    },
    setinLineLoader: (state, action: PayloadAction<boolean>) => {
      state.showinLineLoader = action.payload;
    },
  },
});

export const {
  setMedicalPaOrders,
  appendMedicalPaOrders,
  setLoading,
  setError,
  setLastVisible,
  setTotalDocuments,
  resetPagination,
  setinLineLoader,
} = medicalPaOrdersSlice.actions;

export const fetchTotalDocumentsCount =
  (queryConstraints: QueryConstraint[]) => async (dispatch) => {
    try {
      const allDocs = await FirestoreService.getAllDocumentCount(
        FirestoreCollectionReference.medicalPaOrders(),
        ...queryConstraints,
      );

      dispatch(setTotalDocuments(allDocs));
    } catch (error) {
      logDataToConsole("Error fetching total document count", error);
      return 0;
    }
  };

export const fetchMedicalPaOrdersAfterSearch =
  (facilityId: string, searchTerm?: string) => async (dispatch, getState) => {
    const { medicalPaOrders } = getState();
    if (medicalPaOrders.loading) return;

    dispatch(setLoading(true));
    dispatch(resetPagination());

    const searchqueryConstraints: QueryConstraint[] = [
      where("assigned_to.facility_id", "==", facilityId),
      orderBy("created_at", "desc"),
    ];

    if (searchTerm) {
      searchqueryConstraints.push(
        where("search_index", "array-contains", searchTerm.toLowerCase()),
      );
    }

    try {
      const unsubscribe = FirestoreService.fetchQueryCollection<MedicalPaOrder>(
        FirestoreCollectionReference.medicalPaOrders(),
        (data) => {
          dispatch(setMedicalPaOrders(data as MedicalPaOrder[]));
          dispatch(fetchTotalDocumentsCount(searchqueryConstraints));
        },
        (error) => {
          dispatch(setError(error.message));
        },
        ...searchqueryConstraints,
      );

      return unsubscribe;
    } catch (error) {
      dispatch(setError(error as string));
    }
  };

export const fetchMedicalPaOrders =
  (facilityId: string, pageSize: number) => async (dispatch, getState) => {
    const { medicalPaOrders } = getState();
    if (medicalPaOrders.loading) return;

    dispatch(setLoading(true));
    dispatch(resetPagination());

    const extraConstraints: QueryConstraint[] = [
      orderBy("created_at", "desc"),
      where("assigned_to.facility_id", "==", facilityId),
    ];

    try {
      FirestoreService.paginateQuery<MedicalPaOrder>(
        FirestoreCollectionReference.medicalPaOrders(),
        pageSize,
        ({ docs, lastVisible }) => {
          dispatch(setMedicalPaOrders(docs));
          dispatch(setLastVisible(lastVisible));
          dispatch(fetchTotalDocumentsCount(extraConstraints));
        },
        (error) => {
          dispatch(setError(error.message));
        },
        undefined,
        extraConstraints,
      );
    } catch (error) {
      logDataToConsole("Error in fetchMedicalPaOrders", error);
      dispatch(setError(error as string));
    }
  };

export const fetchMoreMedicalPaOrders =
  (facilityId: string, pageSize: number) => async (dispatch, getState) => {
    const { medicalPaOrders } = getState();

    if (
      medicalPaOrders.loading ||
      medicalPaOrders.showinLineLoader ||
      medicalPaOrders.currentlyLoadedCount === medicalPaOrders.totalDocuments
    ) {
      return;
    }

    dispatch(setinLineLoader(true));

    const extraConstraints: QueryConstraint[] = [
      orderBy("created_at", "desc"),
      where("assigned_to.facility_id", "==", facilityId),
    ];

    try {
      const unsubscribe = FirestoreService.paginateQuery<MedicalPaOrder>(
        FirestoreCollectionReference.medicalPaOrders(),
        pageSize,
        ({ docs, lastVisible }) => {
          dispatch(appendMedicalPaOrders(docs));
          dispatch(setLastVisible(lastVisible));
          dispatch(setinLineLoader(false));
        },
        (error) => {
          dispatch(setError(error.message));
          dispatch(setinLineLoader(false));
        },
        medicalPaOrders.lastVisible,
        extraConstraints,
      );

      return unsubscribe;
    } catch (error) {
      dispatch(setError(error as string));
      dispatch(setinLineLoader(false));
    }
  };

export const hasMoreDataToLoad = (state, endIndex: number) => {
  const { medicalPaOrders } = state;

  return (
    medicalPaOrders.currentlyLoadedCount < medicalPaOrders.totalDocuments &&
    endIndex === medicalPaOrders.currentlyLoadedCount - 1
  );
};

export default medicalPaOrdersSlice.reducer;
